/**
 * MergeIncidentModal — picker for marking the current incident as a duplicate
 * of another. The CURRENT incident becomes the source (gets MERGED status);
 * the picked one is the primary that absorbs chat + sitrep content.
 *
 * Restricted to Admin/Manager (button is hidden for USERs in IncidentChat).
 *
 * Similarity gating: candidates are filtered to a 72-hour window and scored
 * with the same jaccard-based metric the intake banner uses. Each row shows
 * its match %; rows below 0.15 are hidden. The confirm button requires a
 * typed "MERGE" in the warning textbox when similarity < 0.30 — stops the
 * "I clicked the wrong incident and nuked my SitRep" class of mistake.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Incident } from '../types/incident';
import { listIncidents, mergeIncidents } from '../api/incidents';
import { scoreSimilarity } from '../utils/incidentSimilarity';

interface Props {
  current:   Incident;
  onClose:   () => void;
  onMerged:  (primaryId: string) => void;
}

const RECENT_WINDOW_MS    = 72 * 60 * 60 * 1000;
const HIDE_BELOW_SCORE    = 0.15;
const WARN_BELOW_SCORE    = 0.30;
const CONFIRM_TOKEN       = 'MERGE';

function scoreColour(score: number): string {
  if (score >= 0.45) return '#16a34a';
  if (score >= 0.30) return '#f59e0b';
  return '#dc2626';
}

export function MergeIncidentModal({ current, onClose, onMerged }: Props) {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [filter, setFilter]       = useState('');
  const [selected, setSelected]   = useState<Incident | null>(null);
  const [typedConfirm, setTypedConfirm] = useState('');
  const [loading, setLoading]     = useState(true);
  const [merging, setMerging]     = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    setLoading(true);
    listIncidents()
      .then((all) => {
        const cutoff = Date.now() - RECENT_WINDOW_MS;
        setIncidents(
          all.filter((i) => {
            if (i.id === current.id) return false;
            if (i.status === 'CLOSED' || i.status === 'MERGED' || i.status === 'RESOLVED') return false;
            // Don't lock by type — one person may classify as
            // "security threat" while another logs it as "data breach"
            // for the same event. Similarity score + low-match confirm
            // handles the actual gating.
            const t = new Date(i.occurredAt).getTime();
            return Number.isFinite(t) && t >= cutoff;
          }),
        );
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [current.id]);

  // Score every candidate once per render so the list is sorted most-
  // similar-first and we can show a match % per row.
  const scored = useMemo(() => {
    return incidents
      .map((i) => ({
        incident: i,
        score: scoreSimilarity(
          { type: current.type, title: current.title, location: current.location, description: current.description },
          i,
        ),
      }))
      .filter((r) => r.score >= HIDE_BELOW_SCORE)
      .sort((a, b) => b.score - a.score);
  }, [incidents, current.type, current.title, current.location, current.description]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return scored;
    return scored.filter(({ incident: i }) =>
      i.id.toLowerCase().includes(q) ||
      i.title.toLowerCase().includes(q) ||
      i.location.toLowerCase().includes(q)
    );
  }, [scored, filter]);

  const selectedScore = useMemo(() => {
    if (!selected) return 0;
    return scored.find((s) => s.incident.id === selected.id)?.score ?? 0;
  }, [selected, scored]);

  const needsConfirm = !!selected && selectedScore < WARN_BELOW_SCORE;
  const confirmOK    = !needsConfirm || typedConfirm.trim().toUpperCase() === CONFIRM_TOKEN;

  async function confirm() {
    if (!selected || !confirmOK) return;
    setMerging(true);
    setError('');
    try {
      await mergeIncidents(selected.id, current.id);
      onMerged(selected.id);
    } catch (e: any) {
      setError(e.message || 'Merge failed');
    } finally {
      setMerging(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1004,
        background: 'rgba(12,12,18,0.62)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'zipModalFadeIn 180ms ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)', border: '1px solid var(--border-md)',
          borderRadius: 14, padding: '22px 24px', maxWidth: 640, width: '92%',
          maxHeight: '84vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          animation: 'zipModalSlideIn 220ms cubic-bezier(0.34,1.2,0.64,1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>
            Mark {current.id} as duplicate of…
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer', padding: 0 }}
          >×</button>
        </div>

        <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 14px 0', lineHeight: 1.55 }}>
          Showing open incidents from the last 72 h, sorted by similarity. Cross-type matches are allowed
          (e.g. "security threat" vs "data breach" for the same event) but the lower the match %, the more likely
          it's a mistake — a typed confirmation is required below 30%.
          Chat history and SitRep facts will be merged onto the primary; this incident becomes read-only with a
          pointer back. The action is logged on both timelines.
        </p>

        <input
          type="text"
          placeholder="Search by ID, title, location…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'var(--surface)', border: '1px solid var(--border-md)',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--fg)',
            fontFamily: 'inherit', marginBottom: 12,
          }}
        />

        {error && <p style={{ color: 'var(--accent-red)', fontSize: 12 }}>⚠ {error}</p>}
        {loading ? (
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>
            No similar open incidents in the last 72 h. If you're sure this is a duplicate of an older or different-type
            incident, you'll need an admin to reclassify it first.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
            {filtered.map(({ incident: i, score }) => {
              const isSelected = selected?.id === i.id;
              const pct        = Math.round(score * 100);
              return (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => { setSelected(i); setTypedConfirm(''); }}
                  style={{
                    textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                    background: isSelected ? 'rgba(8,145,178,0.10)' : 'var(--surface)',
                    border: `1px solid ${isSelected ? 'rgba(8,145,178,0.55)' : 'var(--border)'}`,
                    borderRadius: 8, padding: '10px 12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>{i.id}</span>
                    <span style={{ fontSize: 12, color: 'var(--fg)' }}>{i.title}</span>
                    <span
                      title="Similarity score — higher means this is more likely the same incident"
                      style={{
                        fontSize: 10, fontWeight: 700,
                        color: scoreColour(score),
                        background: 'rgba(255,255,255,0.05)',
                        border: `1px solid ${scoreColour(score)}55`,
                        padding: '1px 6px', borderRadius: 4,
                        letterSpacing: 0.3,
                      }}
                    >
                      {pct}% MATCH
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>
                      {new Date(i.occurredAt).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                    {i.location} · {i.severity} · {i.status}
                    {i.type !== current.type && (
                      <span style={{ marginLeft: 6, color: '#92400e', fontWeight: 600 }}>
                        · different type ({i.type.replace(/_/g, ' ').toLowerCase()})
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {needsConfirm && (
          <div style={{
            marginTop: 14, padding: '10px 12px',
            background: 'rgba(245,158,11,0.10)',
            border: '1px solid rgba(245,158,11,0.50)',
            borderRadius: 8,
            fontSize: 12, color: 'var(--fg)', lineHeight: 1.55,
          }}>
            <strong style={{ color: '#92400e' }}>Low similarity ({Math.round(selectedScore * 100)}%).</strong>
            {' '}These two incidents don't look very alike. Merging is <em>one-way</em> and rewrites the primary's SitRep.
            Type <strong>{CONFIRM_TOKEN}</strong> below to override and merge anyway.
            <input
              type="text"
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder={`Type ${CONFIRM_TOKEN} to confirm`}
              style={{
                display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 8,
                background: 'var(--surface)', border: '1px solid rgba(245,158,11,0.55)',
                borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--fg)',
                fontFamily: 'inherit', letterSpacing: 0.5,
              }}
            />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={merging}
            style={{
              background: 'transparent', border: '1px solid var(--border-md)',
              color: 'var(--muted)', borderRadius: 6, padding: '6px 14px',
              fontSize: 12, fontWeight: 600, cursor: merging ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >Cancel</button>
          <button
            type="button"
            onClick={confirm}
            disabled={!selected || merging || !confirmOK}
            style={{
              background: selected && confirmOK ? 'rgba(8,145,178,0.18)' : 'var(--surface)',
              border: `1px solid ${selected && confirmOK ? 'rgba(8,145,178,0.55)' : 'var(--border)'}`,
              color: selected && confirmOK ? '#0e7490' : 'var(--muted)',
              borderRadius: 6, padding: '6px 14px',
              fontSize: 12, fontWeight: 700,
              cursor: selected && !merging && confirmOK ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
            }}
          >
            {merging ? 'Merging…' : selected ? `Merge into ${selected.id}` : 'Pick a primary'}
          </button>
        </div>
      </div>
    </div>
  );
}
